import { type KeyboardEvent, useEffect, useMemo, useState } from "react";
import { Form } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { fetchCustomers } from "../../../entities/customer/api/customersApi";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { appNotification } from "../../../shared/notifications/appNotification";
import {
  createPropose,
  fetchPropose,
  fetchEligibleProposeProducts,
  fetchProposeRecipients,
  publishPropose,
  updatePropose,
} from "../api/proposesApi";
import {
  asDay,
  copyProposalItem,
  createScheduleRows,
  datesInPeriod,
  formatRepresentativeId,
  type CompanyOption,
  type MobileItemEditor,
  type ProductOption,
  type ProposalStatus,
  toProposalPayload,
  uniqueProductOptions,
} from "../model/proposeEditor";
import type { ProposeRecipient } from "../model/types";

type UseProposeEditorParams = {
  id?: number;
  representativeId?: string;
  isMobile: boolean;
};

export const useProposeEditor = ({
  id,
  representativeId,
  isMobile,
}: UseProposeEditorParams) => {
  const [form] = Form.useForm();
  const period = Form.useWatch("effective_period", { form, preserve: true }) as
    | [Dayjs, Dayjs]
    | undefined;
  const mobileItems = (Form.useWatch("items", { form, preserve: true }) ||
    []) as Array<Record<string, any>>;
  const scheduleDates = useMemo(() => datesInPeriod(period), [period]);
  const [customers, setCustomers] = useState<CompanyOption[]>([]);
  const [recipients, setRecipients] = useState<ProposeRecipient[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [selectedProductOptions, setSelectedProductOptions] = useState<
    ProductOption[]
  >([]);
  const [noteRows, setNoteRows] = useState<number[]>([]);
  const [openProductRow, setOpenProductRow] = useState<number>();
  const [mobileStep, setMobileStep] = useState<1 | 2 | 3>(1);
  const [mobileItemEditor, setMobileItemEditor] = useState<MobileItemEditor>();
  const [mobileOpenSchedule, setMobileOpenSchedule] = useState<number>(0);
  const [mobileExpandedProducts, setMobileExpandedProducts] = useState<
    Set<number>
  >(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const showError = (error: unknown) =>
    appNotification.error({ message: normalizeApiError(error).message });
  const productOptions = useMemo(
    () => uniqueProductOptions([...products, ...selectedProductOptions]),
    [products, selectedProductOptions],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    void Promise.all([
      fetchCustomers(1, 100),
      fetchProposeRecipients(),
      id ? fetchPropose(id) : Promise.resolve(undefined),
    ])
      .then(([customerPage, recipientRows, proposal]) => {
        if (!active) return;
        setCustomers(
          customerPage.items.map((customer) => {
            const code = formatRepresentativeId(
              `${customer.mt_code}${customer.branch_code ? `-${customer.branch_code}` : ""}`,
            );
            return {
              value: code,
              label: code,
              name:
                customer.company_out_name ||
                customer.company_in_name ||
                "会社名未登録",
            };
          }),
        );
        setRecipients(recipientRows);
        if (proposal) {
          setSelectedProductOptions(
            uniqueProductOptions(
              proposal.items.map((item) => ({
                value: item.product_code,
                name: item.product_name || "",
                label: `${item.product_code} - ${item.product_name || "—"}`,
              })),
            ),
          );
          form.setFieldsValue({
            ...proposal,
            representative_id: formatRepresentativeId(proposal.representative_id),
            effective_period: [
              asDay(proposal.effective_from),
              asDay(proposal.effective_to),
            ],
            estimated_delivery_date: asDay(proposal.estimated_delivery_date),
            estimated_receipt_date: asDay(proposal.estimated_receipt_date),
            expires_at: asDay(proposal.expires_at),
            items: proposal.items.map((item) => ({
              ...item,
              schedules: item.schedules
                .map((schedule) => ({
                  ...schedule,
                  delivery_date: asDay(schedule.delivery_date),
                }))
                .sort(
                  (left, right) =>
                    dayjs(left.delivery_date).valueOf() -
                    dayjs(right.delivery_date).valueOf(),
                ),
            })),
          });
          setNoteRows(
            proposal.items.filter((item) => item.note).map((_, index) => index),
          );
        } else if (representativeId) {
          form.setFieldValue(
            "representative_id",
            formatRepresentativeId(representativeId),
          );
        }
      })
      .catch((error) => {
        if (active) showError(error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [form, id, representativeId]);

  useEffect(() => {
    if (!scheduleDates.length) return;
    const items = form.getFieldValue("items") || [];
    form.setFieldValue(
      "items",
      items.map((item: Record<string, any>) => ({
        ...item,
        schedules: scheduleDates.map((date) => {
          const previous = (item.schedules || []).find((schedule: any) =>
            dayjs(schedule.delivery_date).isSame(date, "day"),
          );
          return {
            delivery_date: date,
            quantity: previous?.quantity ?? 0,
            wholesale_price: previous?.wholesale_price ?? null,
            price: previous?.price ?? null,
          };
        }),
      })),
    );
  }, [form, scheduleDates]);

  const searchProductOptions = async (term = "") => {
    try {
      const result = await fetchEligibleProposeProducts(term);
      setProducts(
        result.map((product) => ({
          value: product.product_code,
          name: product.product_name || "",
          label: `${product.product_code} - ${product.product_name || "—"}`,
        })),
      );
    } catch (error) {
      showError(error);
    }
  };

  const addItem = (add: (value: unknown) => void) =>
    add({ schedules: createScheduleRows(scheduleDates) });

  const selectProduct = (itemIndex: number, value: string) => {
    const selected = productOptions.find((product) => product.value === value);
    if (selected)
      setSelectedProductOptions((current) =>
        uniqueProductOptions([...current, selected]),
      );
    form.setFieldValue(["items", itemIndex, "product_name"], selected?.name);
  };

  const toggleNote = (key: number) =>
    setNoteRows((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );

  const toggleMobileProduct = (key: number) =>
    setMobileExpandedProducts((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const nextMobileStep = async () => {
    try {
      if (mobileStep === 1) {
        await form.validateFields([
          "representative_id",
          "recipient_id",
          "effective_period",
          "estimated_delivery_date",
          "estimated_receipt_date",
        ]);
        setMobileStep(2);
        return;
      }
      if (!form.getFieldValue("items")?.length) {
        appNotification.error({ message: "少なくとも1つの商品を追加してください" });
        return;
      }
      await form.validateFields(["items"]);
      setMobileStep(3);
    } catch {
      // Ant Design displays validation messages next to registered fields.
    }
  };

  const save = async (status: ProposalStatus) => {
    try {
      // On the mobile wizard prior steps are unmounted. Read the full store
      // after validating the currently visible fields.
      await form.validateFields();
      const result = toProposalPayload({
        values: form.getFieldsValue(true),
        status: "DRAFT",
        scheduleDates,
        isMobile,
      });
      if ("errorStep" in result) {
        appNotification.error({ message: result.message });
        if (isMobile) setMobileStep(result.errorStep);
        return undefined;
      }
      setSaving(true);
      const proposal = id
        ? await updatePropose(id, result.payload)
        : await createPropose(result.payload);
      if (status === "PUBLISH") await publishPropose(proposal.propose_id);
      appNotification.success({
        message: status === "PUBLISH" ? "営業担当者へ公開しました" : "下書きを保存しました",
      });
      return proposal.propose_id;
    } catch (error) {
      showError(error);
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const moveQuantityFocus = (
    event: KeyboardEvent<HTMLInputElement>,
    row: number,
    column: number,
    rowCount: number,
  ) => {
    const next = {
      ArrowLeft: [row, column - 1],
      ArrowRight: [row, column + 1],
      ArrowUp: [row - 1, column],
      ArrowDown: [row + 1, column],
    }[event.key];
    if (
      !next ||
      next[0] < 0 ||
      next[0] >= rowCount ||
      next[1] < 0 ||
      next[1] >= scheduleDates.length
    )
      return;
    event.preventDefault();
    document.getElementById(`propose-quantity-${next[0]}-${next[1]}`)?.focus();
  };

  const moveItemFocus = (
    event: KeyboardEvent<HTMLElement>,
    row: number,
    column: number,
    rowCount: number,
  ) => {
    const next = {
      ArrowLeft: [row, column - 1],
      ArrowRight: [row, column + 1],
      ArrowUp: [row - 1, column],
      ArrowDown: [row + 1, column],
    }[event.key];
    if (
      !next ||
      next[0] < 0 ||
      next[0] >= rowCount ||
      next[1] < 0 ||
      next[1] > 9
    )
      return;
    event.preventDefault();
    event.stopPropagation();
    document.getElementById(`propose-item-${next[0]}-${next[1]}`)?.focus();
  };

  return {
    form,
    customers,
    recipients,
    products,
    productOptions,
    scheduleDates,
    mobileItems,
    noteRows,
    openProductRow,
    mobileStep,
    mobileItemEditor,
    mobileOpenSchedule,
    mobileExpandedProducts,
    loading,
    saving,
    addItem,
    copyProposalItem,
    moveItemFocus,
    moveQuantityFocus,
    nextMobileStep,
    save,
    searchProductOptions,
    selectProduct,
    setProducts,
    setSelectedProductOptions,
    setMobileItemEditor,
    setMobileOpenSchedule,
    setMobileExpandedProducts,
    setMobileStep,
    setOpenProductRow,
    setSaving,
    toggleMobileProduct,
    toggleNote,
  };
};
